import type { IDashboardRepository } from '@/core/domain/dashboard/IDashboardRepository'
import { apiService } from '@/core/infrastructure/services/apiServiceFactory'
import { DashboardScoreModule } from '@/core/domain/dashboard/DashboardScoreModule'
import { DashboardScoreDiagnostic } from '@/core/domain/dashboard/DashboardScoreDiagnostic'
import {
  DashboardClassroomDiagnosticTrigger,
  DashboardClassroomDiagnosticTriggerInput
} from '@/core/domain/dashboard/DashboardClassroomDiagnosticTrigger'
import { DashboardClassroomDiagnosticState } from '@/core/domain/dashboard/DashboardClassroomDiagnosticState'
import type { DashboardDiagnosticTriggerRequest } from '@/core/domain/dashboard/DashboardDiagnosticTriggerRequest'
import { EntityValidator } from '@/core/infrastructure/utils/EntityValidator'
import {
  type ClassroomDiagnosticResultsArrayEntity,
  ClassroomDiagnosticResultsArraySchema
} from '@/core/infrastructure/schemas/dashboard/ClassroomDiagnosticResultsSchema'
import { DashboardClassroomModuleResult } from '@/core/domain/dashboard/DashboardClassroomModuleResult'
import { ClassroomDiagnosticStudentResult } from '@/core/domain/dashboard/ClassroomDiagnosticStudentResult'
import type {
  DashboardScoreModuleListEntity
} from '@/core/infrastructure/schemas/dashboard/DashboardScoreModuleSchema.ts'

export class DashboardRepository implements IDashboardRepository {

  async fetchDashboardClassroomScoreModuleResults(
    schoolId: number,
    periodId: number,
    classroomId: number,
    taskId: number
  ): Promise<DashboardScoreModule[]> {
    const response = await apiService.get<DashboardScoreModuleListEntity>(
      `admin/dashboard/schools/${schoolId}/periods/${periodId}/classrooms/${classroomId}/modules/${taskId}`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for dashboard score module results')
    }

    return response.data.map((item) => DashboardScoreModule.create(item))
  }

  async fetchDashboardClassroomScoreDiagnosticResults(
    schoolId: number,
    periodId: number,
    classroomId: number
  ): Promise<DashboardScoreDiagnostic[]> {
    const response = await apiService.get(
      `admin/dashboard/schools/${schoolId}/periods/${periodId}/classrooms/${classroomId}/diagnostic`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for dashboard score diagnostic results')
    }

    return response.data.map((item) => DashboardScoreDiagnostic.create(item))
  }

  async fetchDashboardScoreModuleResults(
    schoolId: number,
    schoolPeriodId: number,
    classroomId: number,
    taskId: number
  ): Promise<DashboardScoreModule[]> {
    return this.fetchDashboardClassroomScoreModuleResults(
      schoolId,
      schoolPeriodId,
      classroomId,
      taskId
    )
  }

  /**
   * Récupère les scores de module des élèves d'une classe
   */
  async fetchDashboardScoreDiagnosticResults(
    schoolId: number,
    schoolPeriodId: number,
    classroomId: number,
    courseId: number
  ): Promise<DashboardScoreDiagnostic[]> {
    const response = await apiService.get(
      `admin/dashboard/schools/${schoolId}/periods/${schoolPeriodId}/classrooms/${classroomId}/courses/${courseId}/diagnostic`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for dashboard score diagnostic results')
    }

    return response.data.map((item) => DashboardScoreDiagnostic.create(item))
  }

  /**
   * Récupère l'ensemble des triggers de diagnostics pour une classe
   */
  async fetchAllDashboardClassroomDiagnosticTriggersByClassroomId(
    classroomId: number
  ): Promise<DashboardClassroomDiagnosticTrigger[]> {
    const response = await apiService.get(
      `admin/dashboard/classrooms/${classroomId}/diagnostic-triggers`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for dashboard classroom diagnostic triggers')
    }

    return response.data.map((item) => DashboardClassroomDiagnosticTrigger.create(item))
  }

  /**
   * Création des Triggers de diagnostics pour une classe et pour une LearningCourse
   */
  async createDashboardClassroomDiagnosticTrigger(
    classroomId: number,
    input: DashboardClassroomDiagnosticTriggerInput
  ): Promise<DashboardClassroomDiagnosticTrigger[]> {
    const response = await apiService.post(
      `admin/dashboard/classrooms/${classroomId}/diagnostic-triggers`,
      {
        schoolClassroomId: input.schoolClassroomId,
        learningCourseName: input.learningCourseName,
        quizType: input.quizType,
        triggerTime: input.triggerTime.toISOString()
      }
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for created diagnostic triggers')
    }

    return response.data.map((item) => DashboardClassroomDiagnosticTrigger.create(item))
  }

  // Nouvelles méthodes basées sur les vraies routes API
  async fetchDashboardClassroomDiagnosticState(
    classroomId: number
  ): Promise<DashboardClassroomDiagnosticState> {
    const response = await apiService.get(`admin/classrooms/${classroomId}/triggers/states`)

    return DashboardClassroomDiagnosticState.create(response.data)
  }

  async triggerDiagnosticStart(
    classroomId: number,
    request: DashboardDiagnosticTriggerRequest
  ): Promise<DashboardClassroomDiagnosticTrigger[]> {
    const response = await apiService.post(
      `admin/classrooms/${classroomId}/triggers/start`,
      request.toApiPayload()
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for diagnostic start trigger')
    }

    return response.data.map((item) => DashboardClassroomDiagnosticTrigger.create(item))
  }

  async triggerDiagnosticEnd(
    classroomId: number,
    request: DashboardDiagnosticTriggerRequest
  ): Promise<DashboardClassroomDiagnosticTrigger[]> {
    const response = await apiService.post(
      `admin/classrooms/${classroomId}/triggers/end`,
      request.toApiPayload()
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for diagnostic end trigger')
    }

    return response.data.map((item) => DashboardClassroomDiagnosticTrigger.create(item))
  }

  async fetchDashboardClassroomAllTriggers(
    classroomId: number
  ): Promise<DashboardClassroomDiagnosticTrigger[]> {
    const response = await apiService.get(`admin/classrooms/${classroomId}/quizzes/triggers`)

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for all diagnostic triggers')
    }

    return response.data.map((item) => DashboardClassroomDiagnosticTrigger.create(item))
  }

  async fetchDashboardClassroomTriggerByQuiz(
    classroomId: number,
    quizId: number
  ): Promise<DashboardClassroomDiagnosticTrigger> {
    const response = await apiService.get(
      `admin/classrooms/${classroomId}/quizzes/${quizId}/triggers`
    )

    return DashboardClassroomDiagnosticTrigger.create(response.data)
  }

  /**
   * Récupère les résultats de diagnostic d'une classe
   * GET /api/admin/schools/{schoolId}/periods/{periodId}/classrooms/{schoolClassroomId}/diagnostics
   */
  async fetchClassroomDiagnosticResults(
    schoolId: number,
    periodId: number,
    schoolClassroomId: number
  ): Promise<ClassroomDiagnosticStudentResult[]> {
    const response = await apiService.get<ClassroomDiagnosticResultsArrayEntity>(
      `admin/schools/${schoolId}/periods/${periodId}/classrooms/${schoolClassroomId}/diagnostics`
    )

    // Valider la réponse avec le schéma des diagnostics
    const validatedData = EntityValidator.validate(
      ClassroomDiagnosticResultsArraySchema,
      response.data,
      'DashboardRepository.fetchClassroomDiagnosticResults'
    )

    return ClassroomDiagnosticStudentResult.createMany(validatedData)
  }

  /**
   * Récupère les résultats des modules d'une classe
   * GET /api/admin/schools/{schoolId}/periods/{periodId}/classrooms/{schoolClassroomId}/results
   * @returns ClassroomModuleResult[]
   */
  async fetchClassroomModuleResults(
    schoolId: number,
    periodId: number,
    schoolClassroomId: number
  ): Promise<DashboardClassroomModuleResult[]> {
    const response = await apiService.get(
      `admin/schools/${schoolId}/periods/${periodId}/classrooms/${schoolClassroomId}/results`
    )

    if (!Array.isArray(response.data)) {
      throw new Error('Expected array response for classroom module results')
    }

    return DashboardClassroomModuleResult.createMany(response.data)
  }
}
